import Cookies from "universal-cookie";
import { Fragment, useState } from "react";
import { ProfileAreaWrapper } from "./styled";
import { useQuery } from "@tanstack/react-query";
import avatar from "../../asset/image/demo-avatar.svg";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { retrieveLoggedInUserService } from "../../util/usermanagement/retrieveLoggedInUser";
import { ProfileFormModal } from "../forms/profile";
import { useNavigate, useParams } from "react-router-dom";
import { signOutUserService } from "../../util/authentication/signOut";
import { retrieveAllOrderService } from "../../util/order/retrieveAllOrder";
import { formatAmountDisplay } from "../../helper/formatAmountDisplay";
import { BaseButton } from "../../component/button/styled";
import { ArrowBack } from "@mui/icons-material";

export const ProfileArea = () => {
	const tabKeys = ["User Profile", "Orders", "Log Out"];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { tabIndex } = useParams();

	const [activeForm, setActiveForm] = useState<string | null>(null);
	const [user, setUser] = useState<Record<string, any> | null>(null);
	const [activeTab, setActiveTab] = useState(
		tabKeys[Number(tabIndex?.trim()) ? Number(tabIndex?.trim()) : 0],
	);

	const fetchLoggedInUser = async () => {
		const loggedInUser = await retrieveLoggedInUserService(TOKEN);
		return loggedInUser;
	};
	const { data: profile } = useQuery({
		queryKey: ["profile", TOKEN, user],
		queryFn: () => fetchLoggedInUser(),
	});

	const fetchAllOrder = async () => {
		const allOrder = await retrieveAllOrderService(TOKEN);
		return allOrder;
	};
	const { data: orders } = useQuery({
		queryKey: ["orders", TOKEN, user],
		queryFn: () => fetchAllOrder(),
	});

	const handleLogOutUser = async () => {
		try {
			const response = await signOutUserService(TOKEN);
			if (response.status === "success") {
				cookies.remove("TOKEN", { path: "/" });
				navigate("/", { replace: true });
			} else {
				console.error("Logout failed. Try again");
			}
		} catch (error: any) {
			console.error("Logout failed, Contact Admin:", error);
		}
	};

	const handleTabClick = (
		e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
		tabKey: string,
	) => {
		e.preventDefault();
		if (activeTab === tabKey || tabKey === "Log Out") return handleLogOutUser();
		setActiveTab(tabKey);
	};

	const handleOpenFormModal = (
		e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
		id: string,
	) => {
		e.preventDefault();
		switch (id) {
			case "profile-photo":
				setUser({ avatar: profile?.avatar || null });
				break;
			case "personal-information":
				setUser({
					firstName: profile?.firstName || "",
					lastName: profile?.lastName || "",
					email: profile?.email || "",
					phone: profile?.phone || "",
				});
				break;
			case "delivery-address":
				setUser({
					country: profile?.country || "Nigeria",
					state: profile?.location || "",
					street: profile?.street || "",
					landmark: profile?.landmark || "",
					houseNumber: profile?.houseNumber || "",
				});
				break;
			default:
				setUser(null);
				break;
		}
		return setActiveForm(id);
	};

	const handleCloseFormModal = () => {
		setActiveForm(null);
		return setUser(null);
	};

	const handleNavigateToOrderDetails = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		orderId: string,
	) => {
		e.preventDefault();
		return navigate(`/order/${orderId}`);
	};

	return (
		<ProfileAreaWrapper>
			{!!activeForm && (
				<ProfileFormModal
					user={user}
					open={!!activeForm}
					id={activeForm || ""}
					handleClose={handleCloseFormModal}
				/>
			)}
			<Box>
				<Typography
					variant="subtitle1"
					fontFamily={"Inter"}
					fontWeight={400}
					fontSize={18}
					lineHeight={"normal"}
					textAlign={"left"}
					whiteSpace={"normal"}
					color={"var(--primary-color)"}
				>
					Account
				</Typography>
				<Typography
					variant="h2"
					fontFamily={"Cormorant"}
					fontWeight={500}
					fontSize={36}
					lineHeight={"normal"}
					textAlign={"left"}
					whiteSpace={"normal"}
					color={"var(--primary-color)"}
				>
					Account Settings
				</Typography>
			</Box>
			<Stack className="tab-area">
				<Stack className="tab-keys">
					{tabKeys.map((tabKey, index) => (
						<Box key={index} className="tab-key">
							<Typography
								variant="subtitle2"
								fontFamily={"Inter"}
								fontWeight={600}
								fontSize={16}
								lineHeight={"normal"}
								textAlign={"left"}
								whiteSpace={"normal"}
								color={
									activeTab === tabKey
										? "var(--primary-color)"
										: "var(--subtitle-gray-color)"
								}
								sx={{
									cursor: activeTab === tabKey ? "none" : "pointer",
								}}
								onClick={(e) => handleTabClick(e, tabKey)}
							>
								{tabKey}
							</Typography>
						</Box>
					))}
				</Stack>
				<Stack className="tab-content">
					{activeTab === tabKeys[0] && (
						<Fragment>
							<Stack className="content-area">
								<Stack className="user-info">
									<Box component={"div"} className="user-avatar-box">
										<img
											src={profile?.avatar || avatar}
											alt="user-avatar"
											className="user-avatar"
										/>
									</Box>
									<Stack className="user-name-box">
										<Box>
											<Typography
												variant="subtitle1"
												fontFamily={"Inter"}
												fontWeight={500}
												fontSize={20}
												lineHeight={"normal"}
												color={"var(--profile-header-color)"}
												maxWidth={"150px"}
												marginBlockEnd={"calc(var(--basic-margin)/8)"}
											>
												{`${profile?.firstName || "NA"} ${profile?.lastName || ""}`}
											</Typography>
										</Box>
										<Box>
											<Typography
												variant="subtitle2"
												fontFamily={"Inter"}
												fontWeight={400}
												fontSize={20}
												lineHeight={"normal"}
												maxWidth={"90px"}
												color={"var(--profile-text-color)"}
											>
												User
											</Typography>
										</Box>
									</Stack>
								</Stack>
								<Box component={"div"} className="profile-card-call-to-action">
									<Typography
										variant="subtitle2"
										fontFamily={"Inter"}
										fontWeight={600}
										fontSize={16}
										lineHeight={"normal"}
										textAlign={"left"}
										whiteSpace={"normal"}
										sx={{ cursor: "pointer" }}
										color={"var(--primary-color)"}
										onClick={(e) => handleOpenFormModal(e, "profile-photo")}
									>
										Edit Profile Photo
									</Typography>
								</Box>
							</Stack>
							<Stack className="content-area">
								<Stack className="personal-information">
									<Box>
										<Typography
											variant="subtitle1"
											fontFamily={"Inter"}
											fontWeight={500}
											fontSize={20}
											lineHeight={"normal"}
											color={"var(--profile-header-color)"}
										>
											Personal Information
										</Typography>
									</Box>
									<Grid
										container
										component={"div"}
										spacing={"calc(var(--flex-gap)/4)"}
										justifyContent={"space-between"}
									>
										{/* <Grid
											component={"div"}
											className="personal-information-grid-item"
											size={{ mobile: 12, tablet: 6 }}
										>
											<Stack className="personal-information-grid-item-body">
												<Box>
													<Typography
														variant="subtitle2"
														fontFamily={"Inter"}
														fontWeight={500}
														fontSize={18}
														lineHeight={"normal"}
														color={"var(--profile-subtitle-color)"}
													>
														Designation
													</Typography>
												</Box>
												<Box>
													<Typography
														variant="subtitle1"
														fontFamily={"Inter"}
														fontWeight={600}
														fontSize={20}
														lineHeight={"normal"}
														color={"var(--profile-text-color)"}
													>
														Mr
													</Typography>
												</Box>
											</Stack>
										</Grid> */}
										<Grid
											component={"div"}
											className="personal-information-grid-item"
											size={{ mobile: 12, tablet: 6 }}
										>
											<Stack className="personal-information-grid-item-body">
												<Box>
													<Typography
														variant="subtitle2"
														fontFamily={"Inter"}
														fontWeight={500}
														fontSize={18}
														lineHeight={"normal"}
														color={"var(--profile-subtitle-color)"}
													>
														First Name
													</Typography>
												</Box>
												<Box>
													<Typography
														variant="subtitle1"
														fontFamily={"Inter"}
														fontWeight={600}
														fontSize={20}
														lineHeight={"normal"}
														color={"var(--profile-text-color)"}
													>
														{profile?.firstName || "NA"}
													</Typography>
												</Box>
											</Stack>
										</Grid>
										<Grid
											component={"div"}
											className="personal-information-grid-item"
											size={{ mobile: 12, tablet: 6 }}
										>
											<Stack className="personal-information-grid-item-body">
												<Box>
													<Typography
														variant="subtitle2"
														fontFamily={"Inter"}
														fontWeight={500}
														fontSize={18}
														lineHeight={"normal"}
														color={"var(--profile-subtitle-color)"}
													>
														Last Name
													</Typography>
												</Box>
												<Box>
													<Typography
														variant="subtitle1"
														fontFamily={"Inter"}
														fontWeight={600}
														fontSize={20}
														lineHeight={"normal"}
														color={"var(--profile-text-color)"}
													>
														{profile?.lastName || "NA"}
													</Typography>
												</Box>
											</Stack>
										</Grid>
										<Grid
											component={"div"}
											className="personal-information-grid-item"
											size={{ mobile: 12, tablet: 6 }}
										>
											<Stack className="personal-information-grid-item-body">
												<Box>
													<Typography
														variant="subtitle2"
														fontFamily={"Inter"}
														fontWeight={500}
														fontSize={18}
														lineHeight={"normal"}
														color={"var(--profile-subtitle-color)"}
													>
														Email Address
													</Typography>
												</Box>
												<Box>
													<Typography
														variant="subtitle1"
														fontFamily={"Inter"}
														fontWeight={600}
														fontSize={20}
														lineHeight={"normal"}
														color={"var(--profile-text-color)"}
													>
														{profile?.email || "NA"}
													</Typography>
												</Box>
											</Stack>
										</Grid>
										<Grid
											component={"div"}
											className="personal-information-grid-item"
											size={{ mobile: 12, tablet: 6 }}
										>
											<Stack className="personal-information-grid-item-body">
												<Box>
													<Typography
														variant="subtitle2"
														fontFamily={"Inter"}
														fontWeight={500}
														fontSize={18}
														lineHeight={"normal"}
														color={"var(--profile-subtitle-color)"}
													>
														Phone
													</Typography>
												</Box>
												<Box>
													<Typography
														variant="subtitle1"
														fontFamily={"Inter"}
														fontWeight={600}
														fontSize={20}
														lineHeight={"normal"}
														color={"var(--profile-text-color)"}
													>
														{profile?.phone || "NA"}
													</Typography>
												</Box>
											</Stack>
										</Grid>
									</Grid>
								</Stack>
								<Box component={"div"} className="profile-card-call-to-action">
									<Typography
										component={"h6"}
										variant="subtitle2"
										fontFamily={"Inter"}
										fontWeight={600}
										fontSize={16}
										lineHeight={"normal"}
										textAlign={"left"}
										whiteSpace={"normal"}
										sx={{ cursor: "pointer" }}
										color={"var(--primary-color)"}
										onClick={(e) =>
											handleOpenFormModal(e, "personal-information")
										}
									>
										Edit Personal Details
									</Typography>
								</Box>
							</Stack>
							<Stack className="content-area">
								<Stack className="delivery-information">
									<Box>
										<Typography
											variant="subtitle1"
											fontFamily={"Inter"}
											fontWeight={500}
											fontSize={20}
											lineHeight={"normal"}
											color={"var(--profile-header-color)"}
										>
											Delivery Address
										</Typography>
									</Box>
									<Grid
										container
										component={"div"}
										spacing={"calc(var(--flex-gap)/4)"}
										justifyContent={"space-between"}
									>
										<Grid
											component={"div"}
											className="delivery-information-grid-item"
											size={{ mobile: 12, tablet: 6 }}
										>
											<Stack className="delivery-information-grid-item-body">
												<Box>
													<Typography
														variant="subtitle2"
														fontFamily={"Inter"}
														fontWeight={500}
														fontSize={18}
														lineHeight={"normal"}
														color={"var(--profile-subtitle-color)"}
													>
														Country
													</Typography>
												</Box>
												<Box>
													<Typography
														variant="subtitle1"
														fontFamily={"Inter"}
														fontWeight={600}
														fontSize={20}
														lineHeight={"normal"}
														color={"var(--profile-text-color)"}
													>
														Nigeria
													</Typography>
												</Box>
											</Stack>
										</Grid>
										<Grid
											component={"div"}
											className="delivery-information-grid-item"
											size={{ mobile: 12, tablet: 6 }}
										>
											<Stack className="delivery-information-grid-item-body">
												<Box>
													<Typography
														variant="subtitle2"
														fontFamily={"Inter"}
														fontWeight={500}
														fontSize={18}
														lineHeight={"normal"}
														color={"var(--profile-subtitle-color)"}
													>
														City/State
													</Typography>
												</Box>
												<Box>
													<Typography
														variant="subtitle1"
														fontFamily={"Inter"}
														fontWeight={600}
														fontSize={20}
														lineHeight={"normal"}
														color={"var(--profile-text-color)"}
													>
														{profile?.location || "NA"}
													</Typography>
												</Box>
											</Stack>
										</Grid>
										<Grid
											component={"div"}
											className="delivery-information-grid-item"
											size={{ mobile: 12, tablet: 6 }}
										>
											<Stack className="delivery-information-grid-item-body">
												<Box>
													<Typography
														variant="subtitle2"
														fontFamily={"Inter"}
														fontWeight={500}
														fontSize={18}
														lineHeight={"normal"}
														color={"var(--profile-subtitle-color)"}
													>
														House Number
													</Typography>
												</Box>
												<Box>
													<Typography
														variant="subtitle1"
														fontFamily={"Inter"}
														fontWeight={600}
														fontSize={20}
														lineHeight={"normal"}
														color={"var(--profile-text-color)"}
													>
														{profile?.houseNumber || "NA"}
													</Typography>
												</Box>
											</Stack>
										</Grid>
										<Grid
											component={"div"}
											className="delivery-information-grid-item"
											size={{ mobile: 12, tablet: 6 }}
										>
											<Stack className="delivery-information-grid-item-body">
												<Box>
													<Typography
														variant="subtitle2"
														fontFamily={"Inter"}
														fontWeight={500}
														fontSize={18}
														lineHeight={"normal"}
														color={"var(--profile-subtitle-color)"}
													>
														Street Name
													</Typography>
												</Box>
												<Box>
													<Typography
														variant="subtitle1"
														fontFamily={"Inter"}
														fontWeight={600}
														fontSize={20}
														lineHeight={"normal"}
														color={"var(--profile-text-color)"}
													>
														{profile?.street || "NA"}
													</Typography>
												</Box>
											</Stack>
										</Grid>
										<Grid
											component={"div"}
											className="delivery-information-grid-item"
											size={{ mobile: 12, tablet: 6 }}
										>
											<Stack className="delivery-information-grid-item-body">
												<Box>
													<Typography
														variant="subtitle2"
														fontFamily={"Inter"}
														fontWeight={500}
														fontSize={18}
														lineHeight={"normal"}
														color={"var(--profile-subtitle-color)"}
													>
														Nearest Landmark
													</Typography>
												</Box>
												<Box>
													<Typography
														variant="subtitle1"
														fontFamily={"Inter"}
														fontWeight={600}
														fontSize={20}
														lineHeight={"normal"}
														color={"var(--profile-text-color)"}
													>
														{profile?.landmark || "NA"}
													</Typography>
												</Box>
											</Stack>
										</Grid>
									</Grid>
								</Stack>
								<Box component={"div"} className="profile-card-call-to-action">
									<Typography
										component={"h6"}
										variant="subtitle2"
										fontFamily={"Inter"}
										fontWeight={600}
										fontSize={16}
										lineHeight={"normal"}
										textAlign={"left"}
										whiteSpace={"normal"}
										sx={{ cursor: "pointer" }}
										color={"var(--primary-color)"}
										onClick={(e) => handleOpenFormModal(e, "delivery-address")}
									>
										Edit Delivery Address
									</Typography>
								</Box>
							</Stack>
						</Fragment>
					)}
					{activeTab === tabKeys[1] && (
						<Fragment>
							{orders?.map((order: Record<string, any>, index: number) => (
								<Stack key={index} className="order-card">
									<Stack className="order-description">
										<Box component={"div"} className="product-thumbnail-box">
											<img
												src={order?.cartItems[0]?.productImages?.[0]}
												alt={order?.cartItems[0]?.productName}
											/>
										</Box>
										<Stack
											flex={1}
											overflow={"hidden"}
											gap={"calc(var(--flex-gap)/8)"}
										>
											<Box>
												<Typography
													variant="caption"
													fontFamily={"Inter"}
													fontWeight={600}
													fontSize={12}
													lineHeight={"normal"}
													whiteSpace={"normal"}
													color={"var(--subtitle-gray-color)"}
													display={"inline-block"}
													width={"100%"}
												>
													{order?.cartItems?.length ?? 0} items across
													categories -{" "}
													{Array.from(
														new Set(
															order?.cartItems?.map(
																(item: Record<string, any>) =>
																	item?.productCategory,
															),
														),
													).join(", ")}
												</Typography>
												<Typography
													variant="h3"
													component={"h3"}
													fontFamily={"Inter"}
													fontWeight={600}
													fontSize={16}
													lineHeight={"normal"}
													whiteSpace={"normal"}
													sx={{ cursor: "pointer" }}
													color={"var(--off-primary-color)"}
												>
													#{order?.transactionReference || "NA"}
												</Typography>
											</Box>
											<Box>
												{/* <Stack
                                                                        direction="row"
                                                                        gap={"calc(var(--flex-gap)/32)"}
                                                                        marginBlockEnd={"calc(var(--basic-margin)/16)"}
                                                                        overflow={"hidden"}
                                                                    >
                                                                        {[...Array(5)].map((_, i) => (
                                                                            <Fragment key={i}>
                                                                                {i < product?.rating ? (
                                                                                    <StarIcon
                                                                                        sx={{
                                                                                            color: "var(--active-rating-color)",
                                                                                            fontSize: 20,
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    <StarBorderIcon
                                                                                        sx={{
                                                                                            color: "var(--subtitle-gray-color)",
                                                                                            fontSize: 20,
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </Fragment>
                                                                        ))}
                                                                    </Stack> */}
												<Typography
													variant="caption"
													fontFamily={"Inter"}
													fontWeight={600}
													fontSize={16}
													lineHeight={"normal"}
													whiteSpace={"normal"}
													color={"var(--off-primary-color)"}
													display={"inline-block"}
													width={"100%"}
												>
													{`₦${formatAmountDisplay(order?.totalPayable || 0)}`}
												</Typography>
											</Box>
										</Stack>
									</Stack>
									<Stack className="order-item-call-to-action">
										<Box sx={{ display: "flex", overflow: "hidden" }}>
											<BaseButton
												disableElevation
												startIcon={<ArrowBack />}
												colour="var(--primary-color)"
												onClick={(e) =>
													handleNavigateToOrderDetails(e, order?.id)
												}
												sx={{
													padding: "0 !important",
													border: "none",
													"&:hover": { border: "none" },
												}}
											>
												<Typography
													variant={"button"}
													fontFamily={"inherit"}
													fontWeight={"inherit"}
													fontSize={"inherit"}
													lineHeight={"inherit"}
													color={"inherit"}
													textTransform={"inherit"}
												>
													See Details
												</Typography>
											</BaseButton>
										</Box>
										<Box
											sx={{
												display: "flex",
												overflow: "hidden",
												justifyContent: "center",
												padding:
													"calc(var(--basic-padding)/9.5) calc(var(--basic-padding)/5.5)",
												borderRadius: "8px",
												background:
													order?.status === "fulfilled"
														? "var(--in-stock-green-color)"
														: order?.status === "paid"
															? "var(--active-rating-color)"
															: "var(--error-red-color)",
											}}
										>
											<Typography
												variant="caption"
												fontFamily={"Inter"}
												fontWeight={500}
												fontSize={14}
												textAlign={"center"}
												lineHeight={"normal"}
												whiteSpace={"normal"}
												textTransform={"capitalize"}
												color={"var(--light-color)"}
											>
												{order?.status || "NA"}
											</Typography>
										</Box>
									</Stack>
								</Stack>
							))}
						</Fragment>
					)}
				</Stack>
			</Stack>
		</ProfileAreaWrapper>
	);
};
